import type { IsoDate } from './common';
import type { MapBinDetail } from './warehouse';

// The warehouse manager's corrections from the map (depo yöneticisi): a product's free units
// move to another bin ("Taşı"), or a bin's quantity is set to what lies on the shelf
// ("Sayıyı düzelt"), as a count approval would. Both only book ledger rows (inventory_movements):
// a transfer, or adjust_in / adjust_out with the reason below and the manager's note.

/** Why a bin's quantity was corrected on the map. "other" needs a note. */
export const BIN_ADJUST_REASONS = ['count_difference', 'damaged', 'lost', 'found', 'other'] as const;
export type BinAdjustReason = (typeof BIN_ADJUST_REASONS)[number];

export const BIN_ADJUST_REASON_LABELS: Readonly<Record<BinAdjustReason, string>> = {
  count_difference: 'Sayım farkı',
  damaged: 'Hasarlı',
  lost: 'Kayıp',
  found: 'Bulundu',
  other: 'Diğer',
};

/**
 * The reasons that fit a correction's direction: units found on the shelf (up) are a count
 * difference, found or other; units missing (down) a count difference, damaged, lost or other.
 * A damaged or lost unit never adds stock, a found one never takes it away.
 */
export const BIN_ADJUST_REASONS_UP: readonly BinAdjustReason[] = ['count_difference', 'found', 'other'];
export const BIN_ADJUST_REASONS_DOWN: readonly BinAdjustReason[] = ['count_difference', 'damaged', 'lost', 'other'];
/** Whether the reason fits a correction of `difference` units (+ added, - taken off); any reason fits no change. */
export const adjustReasonFits = (reason: BinAdjustReason, difference: number) =>
  difference === 0 || (difference > 0 ? BIN_ADJUST_REASONS_UP : BIN_ADJUST_REASONS_DOWN).includes(reason);

/** inventory_movements.reason of a move made on the map */
export const MAP_MOVE_REASON = 'map_move';
/** inventory_movements.reason of a correction made on the map: "map_adjust:damaged" */
export const mapAdjustReason = (reason: BinAdjustReason) => `map_adjust:${reason}`;

/** A product's lot in a bin: what a map action works on. */
export interface BinStockKey {
  readonly warehouseId: string;
  /** the bin's code (A-01-2-1) or its label (LA010201) */
  readonly bin: string;
  readonly productId: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
}

export interface MoveStockRequest extends BinStockKey {
  /** the target bin's code or label */
  readonly toBin: string;
  readonly qty: number;
  /** the recorded volume or load says no, but the goods fit (as at putaway) */
  readonly acceptCapacity?: boolean;
  /** the target holds another seller's stock (or another product where products are kept apart): "Yine de bu göze taşı" */
  readonly acceptMixing?: boolean;
}

/** Why a move would be refused, as the move itself would say it. */
export interface MoveProblem {
  readonly code: string;
  readonly message: string;
  /** the recorded volume or load is exceeded: the manager may pass it ("Sığdı, yine de taşı") */
  readonly capacity: boolean;
  /**
   * the target holds another seller's stock (or another product where the warehouse keeps products
   * apart): the manager may confirm it ("Yine de bu göze taşı"). With `capacity` too, one confirmation passes both.
   */
  readonly mixing: boolean;
  /** what stands in the way: the target bin (another bin may take the goods), or the source line and its quantity */
  readonly side: 'source' | 'target';
}

/** What the target bin holds and whether the move may go there (nothing is booked). */
export interface MoveCheck {
  readonly target: MapBinDetail;
  /** share of the target's volume used after the move; null when unknown */
  readonly fillAfter: number | null;
  readonly problem: MoveProblem | null;
}

export interface MoveStockResult {
  readonly fromBin: string;
  readonly toBin: string;
  readonly sku: string;
  readonly qty: number;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  /** passed past the recorded volume or load */
  readonly capacityAccepted: boolean;
  /** put into a bin holding another seller's stock (or another product) after the warning */
  readonly mixingAccepted: boolean;
}

export interface SetBinQuantityRequest extends BinStockKey {
  /** units on the shelf now (units already picked into a tote are not on it) */
  readonly newQty: number;
  /**
   * The units the screen showed on the shelf when the manager counted. When a putaway, a pick or
   * another correction has changed them since, the count no longer tells the difference: refused.
   */
  readonly expectedShelf: number;
  readonly reason: BinAdjustReason;
  readonly note: string | null;
}

export interface SetBinQuantityResult {
  readonly bin: string;
  readonly sku: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  /** on the shelf before and after */
  readonly before: number;
  readonly after: number;
  readonly difference: number;
  readonly reason: BinAdjustReason;
}

/** The figures of a bin line a map action works with. */
export interface BinLineFigures {
  /** on hand: in the bin, units already picked into a tote included until they are packed */
  readonly onHand: number;
  /** promised to orders: still to be picked, or picked and waiting to be packed */
  readonly reserved: number;
  /** picked into a tote, not packed yet (part of onHand and of reserved, no longer on the shelf) */
  readonly picked: number;
}

/** Units the manager may move: the ones no order is waiting for. */
export const movableQty = (f: BinLineFigures) => Math.max(0, f.onHand - f.reserved);
/** Units lying on the shelf: on hand without what is already in a tote. */
export const shelfQty = (f: BinLineFigures) => Math.max(0, f.onHand - f.picked);
/** Units on the shelf that orders still wait to pick: the least a correction may leave. */
export const reservedOnShelf = (f: BinLineFigures) => Math.max(0, f.reserved - f.picked);
